import {Injectable} from '@angular/core';
import {EnvironmentConfig} from '@environments/environment';
import {StorageService} from '@services/storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationRedirectService {
  private readonly RETURN_URL_STORAGE_KEY = 'authenticationReturnUrl';
  private readonly EXTERNAL_REDIRECT_STORAGE_KEY = 'authenticationExternalRedirect';
  private pendingReturnUrl: string | null = null;
  private pendingExternalRedirect: string | null = null;

  constructor(private storageService: StorageService) {
  }

  public captureExternalRedirect(url: string | null | undefined): void {
    const redirectUrl = this.getRedirectParameter(url);
    if (!redirectUrl) {
      return;
    }

    this.clearReturnUrl();
    this.clearExternalRedirect();
    if (!this.isAllowedExternalRedirect(redirectUrl)) {
      return;
    }

    this.pendingExternalRedirect = redirectUrl;
    this.storageService.writeValueToSessionStorage(this.EXTERNAL_REDIRECT_STORAGE_KEY, redirectUrl);
  }

  public setReturnUrl(url: string | null | undefined): void {
    if (this.getRedirectParameter(url)) {
      this.clearReturnUrl();
      return;
    }
    if (!this.isSafeReturnUrl(url)) {
      this.clearReturnUrl();
      return;
    }

    this.pendingReturnUrl = url;
    this.storageService.writeValueToSessionStorage(this.RETURN_URL_STORAGE_KEY, url);
  }

  public getReturnUrl(): string | null {
    const storedReturnUrl = this.pendingReturnUrl ?? this.readStoredReturnUrl();
    if (!this.isSafeReturnUrl(storedReturnUrl)) {
      this.clearReturnUrl();
      return null;
    }

    this.pendingReturnUrl = storedReturnUrl;
    return storedReturnUrl;
  }

  public consumeReturnUrl(): string | null {
    const returnUrl = this.getReturnUrl();
    this.clearReturnUrl();
    return returnUrl;
  }

  public getExternalRedirect(): string | null {
    const externalRedirect = this.pendingExternalRedirect ?? this.readStoredExternalRedirect();
    if (!this.isAllowedExternalRedirect(externalRedirect)) {
      this.clearExternalRedirect();
      return null;
    }

    this.pendingExternalRedirect = externalRedirect;
    return externalRedirect;
  }

  public consumeExternalRedirect(): string | null {
    const externalRedirect = this.getExternalRedirect();
    this.clearExternalRedirect();
    return externalRedirect;
  }

  public clearExternalRedirect(): void {
    this.pendingExternalRedirect = null;
    this.storageService.deleteByKeyFromSessionStorage(this.EXTERNAL_REDIRECT_STORAGE_KEY);
  }

  public navigateToExternalRedirect(url: string): void {
    if (this.isAllowedExternalRedirect(url)) {
      window.location.assign(url);
    }
  }

  public clearReturnUrl(): void {
    this.pendingReturnUrl = null;
    this.storageService.deleteByKeyFromSessionStorage(this.RETURN_URL_STORAGE_KEY);
  }

  public isAuthenticationRoute(url: string | null | undefined): boolean {
    const path = this.getPath(url);
    return path === '/login'
      || path === '/samllogin'
      || path === '/agreement'
      || path === '/authorization/resetPassword'
      || path.startsWith('/authorization/resetPassword/');
  }

  private isSafeReturnUrl(url: unknown): url is string {
    return typeof url === 'string'
      && url !== '/'
      && url.startsWith('/')
      && !url.startsWith('//')
      && !url.includes('\\')
      && !this.isAuthenticationRoute(url);
  }

  private isAllowedExternalRedirect(url: unknown): url is string {
    if (typeof url !== 'string' || !url.startsWith('https://')) {
      return false;
    }

    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'https:'
        && (EnvironmentConfig.PortalSettings.allowedExternalRedirectUrls ?? []).includes(url);
    } catch (_error) {
      return false;
    }
  }

  private getRedirectParameter(url: string | null | undefined): string | null {
    if (typeof url !== 'string') {
      return null;
    }

    try {
      const parsedUrl = new URL(url, 'https://portal.invalid');
      const redirectUrl = parsedUrl.searchParams.get('redirect');
      if (redirectUrl) {
        return redirectUrl;
      }

      const queryStart = parsedUrl.hash.indexOf('?');
      if (queryStart >= 0) {
        return new URLSearchParams(parsedUrl.hash.slice(queryStart + 1)).get('redirect');
      }
    } catch (_error) {
      return null;
    }

    return null;
  }

  private readStoredReturnUrl(): string | null {
    try {
      const storedReturnUrl = this.storageService.readValueFromSessionStorage(this.RETURN_URL_STORAGE_KEY);
      return typeof storedReturnUrl === 'string' ? storedReturnUrl : null;
    } catch (_error) {
      return null;
    }
  }

  private readStoredExternalRedirect(): string | null {
    try {
      const storedExternalRedirect = this.storageService.readValueFromSessionStorage(this.EXTERNAL_REDIRECT_STORAGE_KEY);
      return typeof storedExternalRedirect === 'string' ? storedExternalRedirect : null;
    } catch (_error) {
      return null;
    }
  }

  private getPath(url: string | null | undefined): string {
    return typeof url === 'string' ? url.split(/[?#]/, 1)[0] : '';
  }
}
